
export interface TreeNode {
    id: string;
    parentId?: string;
    children: TreeNode[];
    [key: string]: any;
}

/**
 * Builds a hierarchical tree from a flat array of items with parentId links.
 */
export function buildTree<T extends { id: string; parentId?: string; sortOrder?: string }>(
    items: T[],
    options: { parentField?: string; orderField?: string } = {}
): (T & { children: T[] })[] {
    const { parentField = 'parentId', orderField = 'sortOrder' } = options;
    const itemMap = new Map();
    const roots: any[] = [];

    // First pass: create nodes
    items.forEach(item => itemMap.set(item.id, { ...item, children: [] }));

    // Second pass: link parents and children
    items.forEach(item => {
        const parentId = (item as any)[parentField];
        if (parentId && itemMap.has(parentId)) {
            itemMap.get(parentId).children.push(itemMap.get(item.id));
        } else {
            roots.push(itemMap.get(item.id));
        }
    });

    // Helper to recursively sort
    const sortNodes = (nodes: any[]) => {
        nodes.sort((a, b) => ((a as any)[orderField] || '').localeCompare((b as any)[orderField] || ''));
        nodes.forEach(node => {
            if (node.children && node.children.length > 0) sortNodes(node.children);
        });
    };

    sortNodes(roots);
    return roots;
}

/**
 * Gets all descendant IDs (including children, grandchildren, etc.) for a list of starting IDs.
 */
export function getAllDescendantIds(items: { id: string; parentId?: string }[], startIds: string[]): string[] {
    const descendantIds: string[] = [];
    const queue = [...startIds];
    const visited = new Set<string>();

    while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (visited.has(currentId)) continue;
        visited.add(currentId);

        // Find all direct children of currentId
        const children = items.filter(item => item.parentId === currentId);
        children.forEach(child => {
            descendantIds.push(child.id);
            queue.push(child.id);
        });
    }

    return descendantIds;
}

/**
 * Gets all descendant slugs for a list of starting slugs.
 */
export function getAllDescendantSlugs(items: { slug: string; parentId?: string; id: string }[], startSlugs: string[]): string[] {
    const startItems = items.filter(item => startSlugs.includes(item.slug));
    const startIds = startItems.map(item => item.id);

    const descendantIds = getAllDescendantIds(items, startIds);
    const descendantSlugs = items
        .filter(item => descendantIds.includes(item.id))
        .map(item => item.slug);

    return [...startSlugs, ...descendantSlugs];
}
